import {sortByPosition} from 'nutshell-core/utils/sorting/sort-by-position';

import {addPlaceholderBlots} from 'shells/email-editor/placeholder-utils';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';

import type {SalesDocumentVariant} from 'nutshell-graphql-types';
import type {MaterializedEmailSequence as MaterializedEmailSequenceFragment} from 'nutshell-graphql-types';

import type {EmailComposerFormSequenceState, EmailComposerFormTemplate} from './types';

type InexactMaterializedEmailSequenceFragment = MaterializedEmailSequenceFragment;

export function addBlotsToEmailSequence(
    materializedSequence?: MaterializedEmailSequenceFragment | null
): any {
    if (!materializedSequence) return undefined;

    return {
        ...materializedSequence,
        materializedEmailSequenceTemplateMessages:
            materializedSequence.materializedEmailSequenceTemplateMessages.map((message) => {
                return {
                    ...message,
                    materializedEmailTemplate: {
                        ...message.materializedEmailTemplate,
                        body: addPlaceholderBlots(message.materializedEmailTemplate.body),
                    },
                };
            }),
    };
}

/**
 * Redux-form `toAddresses` may be either `{ value: EmailAddressee }[]` (reply / wrapped)
 * or legacy EAV `EmailAddress` rows with `entity` on the root. Materialization needs the
 * CRM entity id from the first recipient.
 */
/**
 * After materializing a template in reply mode, append the same HTML the user had in
 * "create from scratch" (quoted thread + signature) below the template body.
 */
export function appendReplyQuotedSuffixToSequenceState(
    sequence: EmailComposerFormSequenceState,
    suffixHtml: string | null | undefined
): EmailComposerFormSequenceState {
    if (!suffixHtml || !sequence.emailTemplates) {
        return sequence;
    }

    const sorted = sortByPosition(sequence.emailTemplates);
    const first = sorted[0];
    if (!first) {
        return sequence;
    }

    const id = first.id;
    const currentBody = sequence.emailTemplates[id]?.body || '';
    if (currentBody.includes(suffixHtml)) {
        return sequence;
    }

    const updatedFirst = {
        ...sequence.emailTemplates[id],
        body: `${currentBody}${suffixHtml}`,
    } as EmailComposerFormTemplate;

    return {
        ...sequence,
        emailTemplates: {
            ...sequence.emailTemplates,
            [id]: updatedFirst,
        },
    };
}

export function getMaterializationEntityIdFromToAddresses(
    toAddresses: unknown,
    relatedLeadIdFallback?: string | null
): string | null {
    if (!Array.isArray(toAddresses) || toAddresses.length === 0) {
        return relatedLeadIdFallback ?? null;
    }

    const first = toAddresses[0] as {
        value?: {entity?: {id: string} | null};
        entity?: {id: string} | null;
    };

    const fromWrapped = first?.value?.entity?.id;
    const fromLegacy = first?.entity?.id;

    return fromWrapped ?? fromLegacy ?? relatedLeadIdFallback ?? null;
}

export function getReplyMessageTemplateIds(
    materializedSequence: InexactMaterializedEmailSequenceFragment
): string[] {
    const replyMaterializedTemplateMessages =
        materializedSequence.materializedEmailSequenceTemplateMessages.filter((message) => {
            return message.emailSequenceTemplateMessage.sendAsReply;
        });

    return replyMaterializedTemplateMessages.map((message) => {
        return message.emailSequenceTemplateMessage.emailTemplate.id;
    });
}

export function getSalesDocumentVariantFromType(
    type?: SalesDocumentType | null
): SalesDocumentVariant | null | undefined {
    if (type === 'Quote') {
        return 'QUOTE';
    } else if (type === 'Invoice') {
        return 'INVOICE';
    }

    return null;
}
